import { useCheckCampaignName } from '@/entities/campaign';
import { DatePicker } from '@/shared/components/date-picker';
import { Input } from '@/shared/components/shadcn/input';
import { Switch } from '@/shared/components/shadcn/switch';
import { cn } from '@/shared/utils';
import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

import { FieldInfoIcon, FieldSection, FormSectionHeader } from '../../primitives';
import { campaignInfoIssueAtom } from '../../store/campaign-editor-issues';
import type { CampaignBasicDraft } from '../../types';
import { useCampaignBasicsName, useDebouncedValue } from './hooks/use-campaign-basics-name';
import { useCampaignBasicsPeriod } from './hooks/use-campaign-basics-period';
import { useCampaignBasicsRefs } from './hooks/use-campaign-basics-refs';

type Props = {
  draft: CampaignBasicDraft;
  initialCampaignName?: string;
  startDateEditable?: boolean;
};

const CAMPAIGN_NAME_AVAILABILITY_DEBOUNCE_MS = 500;

type CampaignNameAvailability = {
  message?: string;
  name: string;
  status: 'idle' | 'checking' | 'available' | 'duplicate';
};

export function CampaignBasicsSection({
  draft,
  initialCampaignName,
  startDateEditable = true,
}: Props) {
  const { handleCampaignNameChange } = useCampaignBasicsName({
    setCampaignName: draft.setCampaignName,
    setHasEditedCampaignName: draft.setHasEditedCampaignName,
  });
  const setCampaignInfoIssue = useSetAtom(campaignInfoIssueAtom);
  const [campaignNameAvailability, setCampaignNameAvailability] =
    useState<CampaignNameAvailability>({
      name: draft.campaignName.trim(),
      status: 'idle',
    });
  const campaignName = draft.campaignName.trim();
  const initialName = initialCampaignName?.trim() ?? '';
  const debouncedCampaignName = useDebouncedValue(
    campaignName,
    CAMPAIGN_NAME_AVAILABILITY_DEBOUNCE_MS,
  );
  const shouldCheckCampaignName = campaignName.length > 0 && campaignName !== initialName;
  const campaignNameAvailabilityQuery = useCheckCampaignName(
    debouncedCampaignName,
    shouldCheckCampaignName && debouncedCampaignName === campaignName,
  );
  const {
    campaignEndDateError,
    campaignEndDateErrorCode,
    campaignStartDateError,
    campaignStartDateErrorCode,
    endingDateMin,
    handleHasEndDateChange,
    startingDateMax,
    startingDateMin,
    workspaceTimezone,
  } = useCampaignBasicsPeriod({
    campaignEndDate: draft.campaignEndDate,
    campaignName: draft.campaignName,
    campaignStartDate: draft.campaignStartDate,
    hasEndDate: draft.hasEndDate,
    setCampaignEndDate: draft.setCampaignEndDate,
    setHasEndDate: draft.setHasEndDate,
  });
  const { campaignEndDateRef, campaignNameRef, campaignStartDateRef } = useCampaignBasicsRefs();
  const campaignNameError =
    campaignNameAvailability.name === campaignName &&
    campaignNameAvailability.status === 'duplicate'
      ? campaignNameAvailability.message || 'Campaign name already exists.'
      : undefined;
  const campaignNameMessageId = 'campaign-name-error';

  useEffect(() => {
    if (!shouldCheckCampaignName) {
      setCampaignNameAvailability({ name: campaignName, status: 'idle' });
      return;
    }

    setCampaignNameAvailability({ name: campaignName, status: 'checking' });
  }, [campaignName, setCampaignNameAvailability, shouldCheckCampaignName]);

  useEffect(() => {
    if (!shouldCheckCampaignName || debouncedCampaignName !== campaignName) return;

    if (campaignNameAvailabilityQuery.isFetching) {
      setCampaignNameAvailability({ name: campaignName, status: 'checking' });
      return;
    }

    if (campaignNameAvailabilityQuery.isSuccess) {
      if (campaignNameAvailabilityQuery.data?.available) {
        setCampaignNameAvailability({ name: campaignName, status: 'available' });
        return;
      } else {
        setCampaignNameAvailability({
          name: campaignName,
          message: 'Campaign name already exists.',
          status: 'duplicate',
        });
        return;
      }
    }

    if (campaignNameAvailabilityQuery.isError) {
      setCampaignNameAvailability({ name: campaignName, status: 'idle' });
    }
  }, [
    campaignName,
    campaignNameAvailabilityQuery.data?.available,
    campaignNameAvailabilityQuery.isError,
    campaignNameAvailabilityQuery.isFetching,
    campaignNameAvailabilityQuery.isSuccess,
    debouncedCampaignName,
    setCampaignNameAvailability,
    shouldCheckCampaignName,
  ]);

  useEffect(() => {
    if (!campaignName) {
      setCampaignInfoIssue({ section: 'campaign-info', reason: 'incomplete' });
      return;
    }

    if (campaignNameAvailability.name === campaignName) {
      if (campaignNameAvailability.status === 'checking') {
        setCampaignInfoIssue({ section: 'campaign-info', reason: 'checking' });
        return;
      }

      if (campaignNameAvailability.status === 'duplicate') {
        setCampaignInfoIssue({ section: 'campaign-info', reason: 'invalid' });
        return;
      }
    }

    if (startDateEditable && campaignStartDateErrorCode) {
      setCampaignInfoIssue({
        section: 'campaign-info',
        reason: campaignStartDateErrorCode === 'required' ? 'incomplete' : 'invalid',
      });
      return;
    }

    if (campaignEndDateErrorCode) {
      setCampaignInfoIssue({
        section: 'campaign-info',
        reason: campaignEndDateErrorCode === 'required' ? 'incomplete' : 'invalid',
      });
      return;
    }

    setCampaignInfoIssue(undefined);
  }, [
    campaignEndDateErrorCode,
    campaignName,
    campaignNameAvailability.name,
    campaignNameAvailability.status,
    campaignStartDateErrorCode,
    setCampaignInfoIssue,
    startDateEditable,
  ]);

  return (
    <section className="space-y-8">
      <div className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-tight">Campaign name</h2>
        <div className="flex flex-col gap-1.5">
          <Input
            id="campaign-name"
            ref={campaignNameRef}
            value={draft.campaignName}
            onChange={handleCampaignNameChange}
            placeholder="e.g. Summer Creator Tracking"
            aria-describedby={campaignNameError ? campaignNameMessageId : undefined}
            aria-invalid={Boolean(campaignNameError)}
            className={cn(
              'h-12 text-base',
              campaignNameError && 'border-destructive ring-destructive/20',
            )}
          />
          {campaignNameError && (
            <p id={campaignNameMessageId} className="text-destructive text-xs" role="alert">
              {campaignNameError}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <FormSectionHeader
          title="Campaign period"
          description={
            startDateEditable
              ? 'Scheduled campaigns can move their starting date from today through the next 30 days. End date is optional, and you can stop the campaign manually later.'
              : 'Starting date is fixed after campaign activation. End date is optional, and you can stop the campaign manually later.'
          }
        />
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <div className="space-y-2">
            <FieldSection
              title="Starting date"
              titleSuffix={
                <FieldInfoIcon
                  ariaLabel="Starting date info"
                  content="Starting date can be set from today through the next 30 days"
                />
              }
            >
              <DatePicker
                ref={campaignStartDateRef}
                value={draft.campaignStartDate || undefined}
                onChange={(date) => date && draft.setCampaignStartDate(date)}
                timeZone={workspaceTimezone}
                disabled={!startDateEditable}
                disabledDates={[{ before: startingDateMin }, { after: startingDateMax }]}
              />
            </FieldSection>
            {startDateEditable && campaignStartDateError ? (
              <p className="text-muted-foreground text-xs">{campaignStartDateError}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <FieldSection
              title="End date"
              trailing={
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground flex items-center text-xs font-normal">
                    <Switch checked={draft.hasEndDate} onCheckedChange={handleHasEndDateChange} />
                  </span>
                </div>
              }
              titleSuffix={
                <FieldInfoIcon
                  ariaLabel="End date info"
                  content="Turn this on if the campaign should stop automatically on a specific date"
                />
              }
            >
              <DatePicker
                ref={campaignEndDateRef}
                value={draft.campaignEndDate}
                onChange={draft.setCampaignEndDate}
                timeZone={workspaceTimezone}
                disabled={!draft.hasEndDate}
                disabledDates={[{ before: endingDateMin }]}
                className={cn(
                  !draft.hasEndDate && '[&_button]:bg-muted [&_button]:text-muted-foreground',
                )}
              />
            </FieldSection>
            {campaignEndDateError ? (
              <p className="text-muted-foreground text-xs">{campaignEndDateError}</p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
